"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GroupDesc = exports.RowDesc = void 0;
const tslib_1 = require("tslib");
const fmko_ts_common_1 = require("fmko-ts-common");
const fmko_ts_widgets_1 = require("fmko-ts-widgets");
const RenderingContext_1 = tslib_1.__importDefault(require("./renderers/RenderingContext"));
const DelegatingGridLayout_1 = tslib_1.__importDefault(require("./DelegatingGridLayout"));
const GridConstants_1 = tslib_1.__importDefault(require("./GridConstants"));
class RowDesc {
    constructor(data, logicalRow) {
        this.data = data;
        this.logicalRow = logicalRow;
    }
}
exports.RowDesc = RowDesc;
class GroupDesc {
    constructor() {
        this.rows = [];
        this.isExpanded = false;
        this.isRendered = false;
    }
}
exports.GroupDesc = GroupDesc;
class AbstractGroupingLayout extends DelegatingGridLayout_1.default {
    constructor(initiallyExpanded) {
        super();
        this.initiallyExpanded = initiallyExpanded;
    }
    data2Tr(data) {
        let result;
        this.groupMap.forEach(group => {
            if (result) {
                return;
            }
            if (group.isExpanded) {
                for (let row of group.rows) {
                    if (data === row.data) {
                        result = row.element;
                    }
                }
            }
        });
        return result || null;
    }
    tr2Data(domRow) {
        let result;
        this.groupMap.forEach(group => {
            if (result) {
                return;
            }
            if (group.element === domRow) {
                result = null;
            }
            if (group.isExpanded) {
                for (const row of group.rows) {
                    if (row.element === domRow) {
                        result = row.data;
                    }
                }
            }
        });
        return result || null;
    }
    tr2DataList(domRow) {
        let result;
        this.groupMap.forEach((group) => {
            if (result) {
                return;
            }
            if (group.element === domRow) {
                let rows = [];
                for (let row of group.rows) {
                    rows.push(row.data);
                }
                result = rows;
            }
            if (group.isExpanded) {
                for (let row of group.rows) {
                    if (row.element === domRow) {
                        result = [row.data];
                    }
                }
            }
        });
        return result || null;
    }
    numInitialDomRows() {
        const rowsAddedByDelegate = this.delegate.numInitialDomRows() - this.dataManager.getNumberOfRows();
        return this.initialRows + rowsAddedByDelegate;
    }
    isEmpty() {
        return this.numInitialDomRows() === 0;
    }
    getRowStyle(row) {
        const style = [];
        if (this.groupForDomRow(row)) {
            style.push(GridConstants_1.default.GRID_HEADER_ROW);
        }
        else {
            const rowDesc = this.getRenderedRow(row);
            const delegateStyle = this.delegate.getRowStyle(rowDesc.logicalRow);
            if (delegateStyle?.length > 0) {
                style.push(...delegateStyle);
            }
        }
        if (row === 0) {
            style.push(GridConstants_1.default.GRID_ROW_FIRST);
        }
        if (row === this.numInitialDomRows() - 1) {
            style.push(GridConstants_1.default.GRID_ROW_LAST);
        }
        return style;
    }
    getRowAttributes(row) {
        return null;
    }
    getCellStyle(row, column) {
        if (this.groupForDomRow(row)) {
            if (column === 0) {
                return [];
            }
            return [GridConstants_1.default.GRID_CELL_LAST];
        }
        else {
            const rowDesc = this.getRenderedRow(row);
            return this.delegate.getCellStyle(rowDesc.logicalRow, column);
        }
    }
    getColSpan(row, column) {
        if (this.groupForDomRow(row)) {
            if (column < this.gridConfig.getNumberOfColumns() - 1) {
                return this.getGroupHeaderColSpan(row, column);
            }
            return 1;
        }
        else {
            const rowDesc = this.getRenderedRow(row);
            return this.delegate.getColSpan(rowDesc.logicalRow, column);
        }
    }
    renderCell(tdElement, ctx, row, column) {
        const group = this.groupForDomRow(row);
        if (group) {
            if (column === this.gridConfig.getNumberOfColumns() - 1) {
                this.renderExpandColumn(ctx, tdElement, group);
            }
            else {
                this.renderGroupHeader(ctx, column, this.dataForGroup(group));
                group.initialRow = ctx.row;
                this.rowInGroupRendered = -1;
                this.groupRendered = group;
                this.domRowInGroupRendered = row;
            }
        }
        else {
            this.renderExpandedRow(tdElement, ctx, row, column, this.rowRendered);
        }
    }
    dataChanged() {
        if (!this.groupMap) {
            this.groupMap = new Map();
        }
        else {
            for (let groupDesc of Array.from(this.groupMap.values())) {
                groupDesc.isRendered = false;
            }
        }
        this.groupMap.forEach((group) => {
            // Keep groupMap so that we can keep track of expanded groups when sorting
            group.rows.length = 0;
        });
        let logicalRow = 0;
        let mapChanged = false;
        this.dataManager.getDataRows().forEach((data) => {
            let group = this.getGroup(data); // this.groupMap.get(data);
            if (!group) {
                group = new GroupDesc();
                group.isExpanded = this.initiallyExpanded;
                this.groupMap.set(data, group);
                mapChanged = true;
            }
            const rowDesc = new RowDesc(data, logicalRow);
            group.rows.push(rowDesc);
            logicalRow++;
        });
        this.initialRows = 0;
        const keysToRemove = [];
        this.groupMap.forEach((value, key) => {
            if (value.rows.length === 0) {
                // Obsolete group with no rows
                keysToRemove.push(key);
            }
            else if (value.isExpanded) {
                this.initialRows += value.rows.length;
            }
        });
        keysToRemove.forEach(key => this.groupMap.delete(key));
        // NOTE: Can't use Map.size, as it can't be polyfilled for IE8.
        this.groupMap.forEach(() => {
            this.initialRows += 1;
        });
        if (mapChanged) {
            // sort keys in groupMap according to comparator
            // TODO: This can be made way smarter. Multiple loops = bad idea
            const keysToSort = [];
            this.groupMap.forEach((value, key) => {
                keysToSort.push(key);
            });
            keysToSort.sort((a, b) => this.comparator.compareTo(a, b));
            for (let key of keysToSort) {
                let value = this.groupMap.get(key);
                this.groupMap.delete(key);
                this.groupMap.set(key, value);
            }
        }
        this.delegate.dataChanged(false);
    }
    postRender() {
        // Run through all groups and locate the corresponding tr element
        this.groupMap.forEach((group) => {
            group.element = this.table.getElement(group.initialRow);
            let dataRow = group.initialRow + 1;
            if (group.isExpanded) {
                // Locate tr elements for data rows
                group.rows.forEach((rowDesc) => {
                    rowDesc.element = this.table.getElement(dataRow++);
                    rowDesc.element.setAttribute(GridConstants_1.default.GRID_ATTRIBUTE_DATAROW, "" + rowDesc.logicalRow);
                });
            }
        });
        this.delegate.postRender();
    }
    groupForDomRow(domRow) {
        let idx = 0;
        let result;
        this.groupMap.forEach((group) => {
            if (result) {
                return;
            }
            if (idx === domRow) {
                result = group;
            }
            else {
                idx += 1;
                if (group.isExpanded) {
                    idx += group.rows.length;
                }
            }
        });
        return result || null;
    }
    dataForGroup(group) {
        return group?.rows
            .filter(defined => !!defined)
            .map(rowDescription => rowDescription.data) ?? null;
    }
    isHeaderRow(row) {
        return true;
    }
    expandOrCollapseGroup(group, expandButton) {
        if (group.isExpanded) {
            this.collapseGroup(group);
            expandButton.setAlt("Udvid");
            expandButton.element.setAttribute(fmko_ts_common_1.ARIA.EXPANDED, "false");
        }
        else {
            this.expandGroup(group);
            expandButton.setAlt("Luk");
            expandButton.element.setAttribute(fmko_ts_common_1.ARIA.EXPANDED, "true");
        }
    }
    expandGroup(group) {
        group.isExpanded = true;
        if (group.isRendered) {
            this.setGroupVisible(group, true);
        }
        else {
            const insertPos = this.findInsertPosition(group);
            for (let row = 0; row < group.rows.length; row++) {
                const rowDesc = group.rows[row];
                const insertedDomRow = insertPos + row;
                const insertedRow = this.table.insertRow(insertedDomRow);
                rowDesc.element = insertedRow;
                rowDesc.element.setAttribute(GridConstants_1.default.GRID_ATTRIBUTE_DATAROW, `${rowDesc.logicalRow}`);
                for (let cellIndex = 0; cellIndex < this.gridConfig.getNumberOfColumns(); cellIndex++) {
                    const tdElement = this.table.ensureCell({
                        row: insertedDomRow,
                        cellIndex: cellIndex
                    });
                    this.table.ensureHeaderFilter(tdElement, cellIndex);
                    const cellStyle = this.delegate.getCellStyle(rowDesc.logicalRow, cellIndex);
                    if (cellStyle?.length > 0) {
                        tdElement.classList.add(...cellStyle);
                    }
                    const ctx = new RenderingContext_1.default();
                    this.delegate.renderCell(tdElement, ctx, rowDesc.logicalRow, cellIndex);
                    tdElement.innerHTML = fmko_ts_common_1.HTMLUtils.sanitizeHTML(ctx.toString());
                    if (ctx.getCell2Widget().length !== 0) {
                        const widget = ctx.getCell2Widget()[0].widget;
                        this.table.setWidget(insertedDomRow, cellIndex, widget);
                        if (!rowDesc.widgets) {
                            rowDesc.widgets = [];
                        }
                        rowDesc.widgets.push(widget);
                    }
                }
                let rowStyle = [];
                let delegateStyle = this.delegate.getRowStyle(rowDesc.logicalRow);
                if (delegateStyle?.length > 0) {
                    rowStyle.push(...delegateStyle);
                }
                if (rowStyle.length) {
                    insertedRow.classList.add(...rowStyle);
                }
                group.isRendered = true;
            }
        }
        group.isExpanded = true;
    }
    collapseGroup(group) {
        this.setGroupVisible(group, false);
        group.isExpanded = false;
    }
    findInsertPosition(group) {
        const parent = group.element.parentElement;
        return Array.from(parent.children).indexOf(group.element) + 1;
    }
    renderExpandColumn(ctx, tdElement, group) {
        tdElement.classList.add(GridConstants_1.default.TD_EXPANDER);
        const expandButton = new fmko_ts_widgets_1.ImageWidget({
            alt: "Udvid",
            imageDimension: fmko_ts_widgets_1.ImageDimensions.XL,
            src: fmko_ts_common_1.ImageSrcWithVersion.getImage(fmko_ts_common_1.ImageSrc.EXPAND)
        });
        expandButton.element.setAttribute(fmko_ts_common_1.ARIA.EXPANDED, "false");
        fmko_ts_common_1.WCAG.addButtonEvent({
            callback: event => {
                let element = this.table.getElementForEvent(event);
                while (!(element instanceof HTMLTableElement)) {
                    if (element instanceof HTMLTableRowElement) {
                        group.element = element;
                        break;
                    }
                    element = element.parentElement;
                }
                this.expandOrCollapseGroup(group, expandButton);
            },
            element: expandButton.element
        });
        ctx.setWidget(expandButton);
    }
    renderExpandedRow(tdElement, ctx, row, column, rowDesc) {
        this.delegate.renderCell(tdElement, ctx, rowDesc.logicalRow, column);
    }
    setGroupVisible(group, visible) {
        const tempArray = [];
        this.groupMap.forEach((groupDesc) => {
            tempArray.push(groupDesc);
        });
        tempArray.forEach((groupDesc, index, arr) => {
            if (groupDesc === group) {
                let nextGroup = null;
                if (index < arr.length - 1) {
                    nextGroup = arr[index + 1];
                }
                else {
                    // No next group, i.e., this is the last group.
                    // We have to move the "last" style from group header to expanded row or vice versa
                    if (visible) {
                        try {
                            if (group.element?.classList.contains(GridConstants_1.default.GRID_ROW_LAST)) {
                                // Move the "last" style from the last element of the group from the group header
                                this.table.getElement(this.table.getRowCount() - 1).classList.add(GridConstants_1.default.GRID_ROW_LAST);
                            }
                        }
                        catch (ex) {
                            throw new Error(`Problem moving "${GridConstants_1.default.GRID_ROW_LAST}" style from group header, group.element=${group.element}`);
                        }
                    }
                    else {
                        try {
                            if (this.table.getElement(this.table.getRowCount() - 1)?.classList.contains(GridConstants_1.default.GRID_ROW_LAST)) {
                                // Move the "last" style from the last element of the group to the group header
                                group.element.classList.add(GridConstants_1.default.GRID_ROW_LAST);
                            }
                        }
                        catch (ex) {
                            const tbl = this.table;
                            throw new Error(`Problem moving "${GridConstants_1.default.GRID_ROW_LAST}" style to group header, table.getRowCount()=${tbl.getRowCount()},
                                ${tbl.getElement(tbl.getRowCount() - 1)}=${tbl.getElement(tbl.getRowCount() - 1)}`);
                        }
                    }
                }
                for (let tr = group.element.nextElementSibling; nextGroup ? tr !== nextGroup.element : tr; tr = tr.nextElementSibling) {
                    (0, fmko_ts_common_1.setElementVisible)(tr, visible);
                }
            }
        });
    }
    getRenderedRow(renderedDomRow) {
        if (this.domRowInGroupRendered !== renderedDomRow || !this.rowRendered) {
            // Detect that rendering moved on to a new row
            this.domRowInGroupRendered = renderedDomRow;
            this.rowInGroupRendered++;
            this.rowRendered = this.groupRendered.rows[this.rowInGroupRendered];
        }
        return this.rowRendered;
    }
    // helper method to lookup group by using comparator
    getGroup(key) {
        let result;
        this.groupMap.forEach((g, k) => {
            if (result) {
                return;
            }
            if (this.comparator.compareTo(k, key) === 0) {
                result = g;
            }
        });
        return result;
    }
}
exports.default = AbstractGroupingLayout;
//# sourceMappingURL=AbstractGroupingLayout.js.map